import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Send, Loader2, Check, CheckCheck, Bot } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Mensaje {
  id: string;
  autor_id: string;
  mensaje: string;
  created_at: string;
  leido_at: string | null;
  es_sistema: boolean;
  autor_nombre?: string;
  autor_apellido?: string;
}

interface ChatAtencionProps {
  citaId?: string;
  requestId?: string;
}

export function ChatAtencion({ citaId, requestId }: ChatAtencionProps) {
  const { user } = useAuth();
  const [mensajes, setMensajes] = useState<Mensaje[]>([]);
  const [nuevoMensaje, setNuevoMensaje] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [otherTyping, setOtherTyping] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const typingChannelRef = useRef<any>(null);

  const channelId = citaId ? `chat-cita-${citaId}` : `chat-req-${requestId}`;
  const filterCol = citaId ? 'cita_id' : 'request_id';
  const filterVal = citaId || requestId || '';

  // Set up realtime for messages + typing indicator
  useEffect(() => {
    fetchMensajes();

    // Message channel
    const msgChannel = supabase
      .channel(channelId)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'mensajes_atencion',
        filter: `${filterCol}=eq.${filterVal}`,
      }, () => {
        fetchMensajes();
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'mensajes_atencion',
        filter: `${filterCol}=eq.${filterVal}`,
      }, () => {
        fetchMensajes();
      })
      .subscribe();

    // Typing indicator channel (Supabase Broadcast)
    const typingChannel = supabase
      .channel(`typing-${filterVal}`)
      .on('broadcast', { event: 'typing' }, (payload) => {
        if (payload.payload?.user_id !== user?.id) {
          setOtherTyping(true);
          if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = setTimeout(() => setOtherTyping(false), 3000);
        }
      })
      .subscribe();

    typingChannelRef.current = typingChannel;

    return () => {
      supabase.removeChannel(msgChannel);
      supabase.removeChannel(typingChannel);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, [citaId, requestId]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [mensajes, otherTyping]);

  // Mark messages as read when viewing
  useEffect(() => {
    if (!user?.id || mensajes.length === 0) return;
    const unread = mensajes.filter(m => m.autor_id !== user.id && !m.leido_at && !m.es_sistema);
    if (unread.length === 0) return;

    const ids = unread.map(m => m.id);
    supabase
      .from('mensajes_atencion')
      .update({ leido_at: new Date().toISOString() })
      .in('id', ids)
      .then(() => {
        // Update local state
        setMensajes(prev => prev.map(m =>
          ids.includes(m.id) ? { ...m, leido_at: new Date().toISOString() } : m
        ));
      });
  }, [mensajes, user?.id]);

  const fetchMensajes = async () => {
    try {
      let query = supabase
        .from('mensajes_atencion')
        .select('id, autor_id, mensaje, created_at, leido_at, es_sistema')
        .order('created_at', { ascending: true });

      if (citaId) query = query.eq('cita_id', citaId);
      if (requestId) query = query.eq('request_id', requestId);

      const { data, error } = await query;
      if (error) throw error;

      if (!data || data.length === 0) {
        setMensajes([]);
        setLoading(false);
        return;
      }

      const autorIds = [...new Set(data.filter(m => !m.es_sistema).map(m => m.autor_id))];
      let profileMap = new Map();
      if (autorIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id, nombre, apellido')
          .in('user_id', autorIds);
        profileMap = new Map((profiles || []).map(p => [p.user_id, p]));
      }

      const result: Mensaje[] = data.map(m => {
        const profile = profileMap.get(m.autor_id) as any;
        return {
          ...m,
          autor_nombre: m.es_sistema ? 'Sistema' : (profile?.nombre || 'Usuario'),
          autor_apellido: m.es_sistema ? '' : (profile?.apellido || ''),
        };
      });

      setMensajes(result);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const broadcastTyping = useCallback(() => {
    typingChannelRef.current?.send({
      type: 'broadcast',
      event: 'typing',
      payload: { user_id: user?.id },
    });
  }, [user?.id]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNuevoMensaje(e.target.value);
    broadcastTyping();
  };

  const handleSend = async () => {
    if (!nuevoMensaje.trim() || !user?.id) return;
    setSending(true);
    try {
      const insertData: any = {
        autor_id: user.id,
        mensaje: nuevoMensaje.trim(),
        es_sistema: false,
      };
      if (citaId) insertData.cita_id = citaId;
      if (requestId) insertData.request_id = requestId;

      const { error } = await supabase.from('mensajes_atencion').insert(insertData);
      if (error) throw error;
      setNuevoMensaje('');
    } catch (err: any) {
      console.error('Error sending:', err.message);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[350px]">
      <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-2 p-3 bg-muted/30 rounded-lg">
        {mensajes.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-8">
            No hay mensajes aún. ¡Inicia la conversación!
          </p>
        ) : (
          mensajes.map((msg) => {
            const isOwn = msg.autor_id === user?.id;

            // System messages
            if (msg.es_sistema) {
              return (
                <div key={msg.id} className="flex justify-center my-2">
                  <div className="flex items-center gap-1.5 bg-muted/80 rounded-full px-3 py-1.5 max-w-[85%]">
                    <Bot className="w-3 h-3 text-muted-foreground shrink-0" />
                    <p className="text-xs text-muted-foreground text-center">{msg.mensaje}</p>
                  </div>
                </div>
              );
            }

            return (
              <div key={msg.id} className={`flex gap-2 ${isOwn ? 'flex-row-reverse' : ''}`}>
                <Avatar className="h-7 w-7 shrink-0">
                  <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                    {msg.autor_nombre?.[0]}{msg.autor_apellido?.[0]}
                  </AvatarFallback>
                </Avatar>
                <div className={`max-w-[75%]`}>
                  <div className={cn(
                    'rounded-2xl px-3 py-2 text-sm',
                    isOwn
                      ? 'bg-primary text-primary-foreground rounded-br-sm'
                      : 'bg-background border rounded-bl-sm'
                  )}>
                    {msg.mensaje}
                  </div>
                  <div className={`flex items-center gap-1 mt-0.5 ${isOwn ? 'justify-end' : ''}`}>
                    <p className="text-[10px] text-muted-foreground">
                      {msg.autor_nombre} · {new Date(msg.created_at).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                    {isOwn && (
                      msg.leido_at
                        ? <CheckCheck className="w-3 h-3 text-primary" />
                        : <Check className="w-3 h-3 text-muted-foreground" />
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}

        {/* Typing indicator */}
        {otherTyping && (
          <div className="flex gap-2 items-center">
            <Avatar className="h-7 w-7 shrink-0">
              <AvatarFallback className="text-[10px] bg-muted text-muted-foreground">...</AvatarFallback>
            </Avatar>
            <div className="bg-background border rounded-2xl rounded-bl-sm px-3 py-2">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-2 mt-2">
        <Input
          placeholder="Escribe un mensaje..."
          value={nuevoMensaje}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={sending}
          className="rounded-full"
        />
        <Button size="icon" className="rounded-full shrink-0" onClick={handleSend} disabled={sending || !nuevoMensaje.trim()}>
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
}
